import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Card,
    CardContent,
    CardMedia,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Snackbar,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CameraIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import ImageIcon from '@mui/icons-material/Image';

const FALLBACK_LIMIT = 10;

const getStorageKey = (itemId) => (itemId ? `clean-image-selection:${itemId}` : null);

const readStoredFilename = (storageKey) => {
    if (!storageKey || typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
        return null;
    }
    try {
        return window.localStorage.getItem(storageKey);
    } catch (error) {
        console.warn('Failed to read clean image selection state', error);
        return null;
    }
};

const persistStoredFilename = (storageKey, filename) => {
    if (!storageKey || typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
        return;
    }
    try {
        if (!filename) {
            window.localStorage.removeItem(storageKey);
        } else {
            window.localStorage.setItem(storageKey, filename);
        }
    } catch (error) {
        console.warn('Failed to persist clean image selection state', error);
    }
};

const findImageByFilename = (list, filename) => {
    if (!filename || !Array.isArray(list)) {
        return null;
    }
    return list.find((entry) => entry.filename === filename) || null;
};

const getCsrfToken = () => {
    if (typeof document === 'undefined') {
        return null;
    }
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || null;
};

const formatBytes = (bytes) => {
    if (!Number.isFinite(bytes) || bytes <= 0) {
        return null;
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, index);
    return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[index]}`;
};

const formatTimestamp = (value) => {
    if (!value) {
        return null;
    }
    try {
        return new Date(value).toLocaleString();
    } catch (error) {
        return value;
    }
};

const buildPreviewUrl = (meta) => {
    if (!meta?.url) {
        return null;
    }
    const bust = Date.now();
    const separator = meta.url.includes('?') ? '&' : '?';
    return `${meta.url}${separator}v=${bust}`;
};

const CleanImageDrawer = ({
    statusUrl,
    captureUrl,
    deleteUrl,
    initialMeta = null,
    itemName = '',
    itemId = '',
    canCapture = true,
    pollDelayMs = 2500,
    pollAttempts = 6,
}) => {
    const storageKey = getStorageKey(itemId);
    const initialImages = Array.isArray(initialMeta?.images) ? initialMeta.images : [];
    const initialStoredFilename = readStoredFilename(storageKey);
    const storedInitialImage = initialStoredFilename ? findImageByFilename(initialImages, initialStoredFilename) : null;
    if (initialStoredFilename && !storedInitialImage) {
        persistStoredFilename(storageKey, null);
    }

    const initialPrimary = initialMeta?.primary ?? initialImages[0] ?? null;
    const initialDisplayedImage = storedInitialImage ?? initialPrimary ?? null;

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [images, setImages] = useState(initialImages);
    const [imageLimit, setImageLimit] = useState(initialMeta?.max_images ?? FALLBACK_LIMIT);
    const [selectedImage, setSelectedImage] = useState(initialDisplayedImage);
    const [canvasImage, setCanvasImage] = useState(initialDisplayedImage);
    const [message, setMessage] = useState(null);
    const [messageTone, setMessageTone] = useState('info');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingDeletion, setPendingDeletion] = useState(null);
    const [snackbar, setSnackbar] = useState({open: false, text: '', tone: 'info'});

    const pushMessage = useCallback((text, tone = 'info') => {
        setMessage(text);
        setMessageTone(tone);
        setSnackbar({open: true, text, tone});
    }, []);

    const handleSnackbarClose = useCallback((_, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar((current) => ({...current, open: false}));
    }, []);

    const syncImagesFromPayload = useCallback((payload) => {
        if (!payload) {
            return;
        }
        const nextImages = Array.isArray(payload.images) ? payload.images : [];
        const nextPrimary = payload.primary ?? null;

        setImages(nextImages);
        setImageLimit(payload.max_images ?? FALLBACK_LIMIT);

        if (!nextImages.length) {
            setSelectedImage(null);
            setCanvasImage(null);
            persistStoredFilename(storageKey, null);
            return;
        }

        const storedFilename = readStoredFilename(storageKey);
        let storedImage = storedFilename ? findImageByFilename(nextImages, storedFilename) : null;
        if (storedFilename && !storedImage) {
            persistStoredFilename(storageKey, null);
        }

        const resolvedPrimary = nextPrimary ? findImageByFilename(nextImages, nextPrimary.filename) : null;

        setCanvasImage((currentCanvas) => {
            if (storedImage) {
                return storedImage;
            }
            if (currentCanvas) {
                const match = findImageByFilename(nextImages, currentCanvas.filename);
                if (match) {
                    return match;
                }
            }
            if (resolvedPrimary) {
                return resolvedPrimary;
            }
            return nextImages[0];
        });

        setSelectedImage((current) => {
            if (current) {
                const match = findImageByFilename(nextImages, current.filename);
                if (match) {
                    return match;
                }
            }
            if (storedImage) {
                return storedImage;
            }
            if (resolvedPrimary) {
                return resolvedPrimary;
            }
            return nextImages[0];
        });
    }, [storageKey]);

    const previewUrl = useMemo(() => buildPreviewUrl(selectedImage), [selectedImage]);
    const sizeLabel = useMemo(() => formatBytes(selectedImage?.size ?? null), [selectedImage]);
    const updatedLabel = useMemo(() => formatTimestamp(selectedImage?.updated_at ?? null), [selectedImage]);
    const limitReached = images.length >= (imageLimit ?? FALLBACK_LIMIT);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        window.dispatchEvent(new CustomEvent('cleanImage:activeChanged', {
            detail: {url: canvasImage?.url || null},
        }));
    }, [canvasImage]);

    const runStatusFetch = useCallback(async () => {
        if (!statusUrl) {
            return null;
        }
        const response = await fetch(statusUrl, {
            headers: {'Accept': 'application/json'},
            credentials: 'same-origin',
        });
        if (!response.ok) {
            throw new Error('Server returned an error');
        }
        const payload = await response.json();
        if (payload?.image) {
            syncImagesFromPayload(payload.image);
        }
        if (payload?.message) {
            pushMessage(payload.message, payload.status === 'ready' ? 'success' : 'info');
        }
        return payload;
    }, [statusUrl, pushMessage, syncImagesFromPayload]);

    const pollUntilReady = useCallback(async (attemptsLeft) => {
        if (attemptsLeft <= 0) {
            setSaving(false);
            return;
        }
        try {
            const payload = await runStatusFetch();
            if (payload?.status === 'ready' && payload?.image?.images?.length) {
                setMessage(payload.message || 'Clean image ready.');
                setMessageTone('success');
                setSaving(false);
                return;
            }
        } catch (error) {
            console.warn('Clean image status polling failed', error);
        }
        setTimeout(() => pollUntilReady(attemptsLeft - 1), pollDelayMs);
    }, [pollDelayMs, runStatusFetch]);

    const handleRefresh = useCallback(async () => {
        if (!statusUrl) {
            return;
        }
        setLoading(true);
        try {
            await runStatusFetch();
            pushMessage('Clean image metadata refreshed.', 'info');
        } catch (error) {
            console.error('Failed to refresh clean image', error);
            pushMessage('Unable to load clean image status right now.', 'error');
        } finally {
            setLoading(false);
        }
    }, [statusUrl, runStatusFetch, pushMessage]);

    const handleCapture = useCallback(async () => {
        if (!captureUrl || !canCapture) {
            return;
        }
        if (limitReached) {
            pushMessage('Maximum number of clean images reached. Delete one before capturing a new image.', 'warning');
            return;
        }
        const csrfToken = getCsrfToken();
        setSaving(true);
        pushMessage('Clean image capture requested. Please wait a few seconds before the preview updates.', 'info');
        try {
            const response = await fetch(captureUrl, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    ...(csrfToken ? {'X-CSRF-TOKEN': csrfToken} : {}),
                },
                credentials: 'same-origin',
                body: JSON.stringify({}),
            });
            const payload = await response.json();
            if (!response.ok) {
                throw new Error(payload?.message || 'Unable to queue clean image capture.');
            }
            if (payload?.image) {
                syncImagesFromPayload(payload.image);
            }
            pushMessage(payload?.message || 'Clean image capture queued. Refresh after a few seconds to view the new image.', 'info');
            pollUntilReady(pollAttempts);
        } catch (error) {
            console.error('Failed to queue clean image capture', error);
            setSaving(false);
            pushMessage(error.message || 'Unable to queue clean image capture.', 'error');
        }
    }, [captureUrl, canCapture, pollAttempts, pollUntilReady, pushMessage, limitReached, syncImagesFromPayload]);

    const handleUseForEditing = useCallback(() => {
        if (!selectedImage) {
            return;
        }
        setCanvasImage(selectedImage);
        persistStoredFilename(storageKey, selectedImage.filename || null);
        pushMessage('Selected clean image is now used on the edit canvas.', 'success');
    }, [selectedImage, storageKey, pushMessage]);

    const requestDelete = useCallback((image) => {
        if (!image) {
            return;
        }
        setPendingDeletion(image);
        setConfirmOpen(true);
    }, []);

    const executeDelete = useCallback(async () => {
        if (!deleteUrl || !pendingDeletion) {
            return;
        }
        const csrfToken = getCsrfToken();
        setDeleting(true);
        pushMessage('Deleting clean image...', 'info');
        try {
            const response = await fetch(deleteUrl, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    ...(csrfToken ? {'X-CSRF-TOKEN': csrfToken} : {}),
                },
                credentials: 'same-origin',
                body: JSON.stringify({filename: pendingDeletion.filename}),
            });
            const payload = await response.json();
            if (!response.ok) {
                throw new Error(payload?.message || 'Unable to delete the clean image.');
            }
            if (payload?.image) {
                syncImagesFromPayload(payload.image);
            }
            pushMessage(payload?.message || 'Delete request sent to the device.', 'success');
        } catch (error) {
            console.error('Failed to delete clean image', error);
            pushMessage(error.message || 'Unable to delete the clean image.', 'error');
        } finally {
            setDeleting(false);
            setConfirmOpen(false);
            setPendingDeletion(null);
        }
    }, [deleteUrl, pendingDeletion, pushMessage, syncImagesFromPayload]);

    const handleOpenDrawer = useCallback(() => {
        setDrawerOpen(true);
        handleRefresh();
    }, [handleRefresh]);

    const handleCloseDrawer = useCallback(() => {
        setDrawerOpen(false);
    }, []);

    const disablePromote = !selectedImage || (canvasImage && selectedImage.filename === canvasImage.filename);
    const disableDelete = !selectedImage || deleting || !deleteUrl;
    const isSelectedActive = selectedImage && canvasImage && selectedImage.filename === canvasImage.filename;

    return (
        <>
            <ButtonGroup size="small" variant="outlined">
                <Tooltip title={canCapture ? (limitReached ? 'Delete an image before capturing a new one' : 'Trigger device capture') : 'Clean image capture unavailable for this item'}>
                    <span>
                        <Button
                            startIcon={<CameraIcon/>}
                            color="secondary"
                            disabled={!canCapture || saving || limitReached}
                            onClick={handleCapture}
                        >
                            Save clean image
                        </Button>
                    </span>
                </Tooltip>
                <Button
                    startIcon={<VisibilityIcon/>}
                    onClick={handleOpenDrawer}
                >
                    View clean images
                </Button>
            </ButtonGroup>

            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={handleCloseDrawer}
                PaperProps={{sx: {width: {xs: '100%', md: 620}}}}
            >
                <Box sx={{p: 3, display: 'flex', flexDirection: 'column', gap: 2, height: '100%'}}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h6">Clean reference images</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {itemName || 'Current flux'}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Tooltip title={selectedImage ? 'Show this clean image in the edit view' : 'Select an image first'}>
                                <span>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        disabled={disablePromote}
                                        onClick={handleUseForEditing}
                                    >
                                        Use for editing
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title={selectedImage ? 'Delete this image from the device' : 'Select an image to delete'}>
                                <span>
                                    <Button
                                        size="small"
                                        startIcon={<DeleteIcon/>}
                                        onClick={() => requestDelete(selectedImage)}
                                        disabled={disableDelete}
                                        variant="contained"
                                        color="error"
                                    >
                                        Delete
                                    </Button>
                                </span>
                            </Tooltip>
                            <Button
                                size="small"
                                startIcon={<RefreshIcon/>}
                                onClick={handleRefresh}
                                disabled={loading}
                            >
                                Refresh
                            </Button>
                        </Stack>
                    </Stack>

                    {message && (
                        <Alert severity={messageTone} onClose={() => setMessage(null)}>
                            {message}
                        </Alert>
                    )}

                    <Card variant="outlined" sx={{flexGrow: 1, display: 'flex', flexDirection: 'column'}}>
                        {previewUrl ? (
                            <CardMedia
                                component="img"
                                src={previewUrl}
                                alt="Clean reference"
                                sx={{maxHeight: 360, objectFit: 'contain', bgcolor: 'grey.100'}}
                            />
                        ) : (
                            <Box sx={{p: 4, textAlign: 'center'}}>
                                <Typography variant="body2" color="text.secondary">
                                    No clean image selected.
                                </Typography>
                            </Box>
                        )}
                        <Divider/>
                        <CardContent>
                            <Stack spacing={1}>
                                <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                                    <Chip label={selectedImage ? 'Available' : 'Missing'} color={selectedImage ? 'success' : 'default'} />
                                    {isSelectedActive && <Chip label="In edit view" color="primary" />}
                                    {selectedImage?.is_primary && <Chip label="Device default" color="info" />}
                                    {sizeLabel && <Chip label={sizeLabel} />}
                                    <Chip label={`${images.length}/${imageLimit} stored`} />
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                    Last captured: {updatedLabel || '—'}
                                </Typography>
                                {selectedImage?.url && (
                                    <Button
                                        href={selectedImage.url}
                                        target="_blank"
                                        rel="noopener"
                                        variant="text"
                                        size="small"
                                    >
                                        Open full size
                                    </Button>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>

                    <Box sx={{flexGrow: 1, overflow: 'auto'}}>
                        {images.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{py: 2}}>
                                No clean images stored for this flux yet.
                            </Typography>
                        ) : (
                            <List dense disablePadding>
                                {images.map((image) => {
                                    const isSelected = selectedImage?.filename === image.filename;
                                    const isActive = canvasImage?.filename === image.filename;
                                    const isDeviceDefault = image.is_primary;
                                    return (
                                        <ListItemButton
                                            key={image.path}
                                            selected={isSelected}
                                            onClick={() => setSelectedImage(image)}
                                        >
                                            <ListItemIcon sx={{minWidth: 32}}>
                                                {isActive ? <CheckCircleIcon color="success" fontSize="small"/> : <ImageIcon fontSize="small"/>}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={image.filename}
                                                secondary={`${formatTimestamp(image.updated_at) || '—'} · ${formatBytes(image.size) || '—'}`}
                                            />
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                {isActive && <Chip label="In edit view" size="small" color="success" />}
                                                {isDeviceDefault && <Chip label="Device default" size="small" color="info" />}
                                            </Stack>
                                        </ListItemButton>
                                    );
                                })}
                            </List>
                        )}
                    </Box>
                </Box>
            </Drawer>

            <Dialog
                open={confirmOpen}
                onClose={() => (deleting ? null : setConfirmOpen(false))}
            >
                <DialogTitle>Delete clean image</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This queues a delete command for <strong>{pendingDeletion?.filename || 'the selected image'}</strong>. Continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button color="error" onClick={executeDelete} disabled={deleting}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.tone} sx={{width: '100%'}}>
                    {snackbar.text}
                </Alert>
            </Snackbar>
        </>
    );
};

export default CleanImageDrawer;
